import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from './components/shared/ProtectedRoute';
import RouteFallback from './components/shared/RouteFallback';
import { venueSlugFromHost, isCustomDomain } from './utils/subdomain';
import BYODomainBoot from './components/shared/BYODomainBoot';

// Layouts (eager — small + always rendered for their route group)
import UserLayout from './components/layout/UserLayout';
import PartnerLayout from './components/layout/PartnerLayout';
import AdminLayout from './components/layout/AdminLayout';

/**
 * Route splitting: user pages are eager (anonymous cold-start path — a
 * Suspense round-trip on first paint would regress LCP). Admin + partner
 * pages and their login pages are React.lazy so anonymous visitors never
 * download those chunks. Suspense fallback = RouteFallback spinner.
 */

// ── User pages (eager — first-paint critical path) ──
import HomePage from './pages/user/HomePage';
import LoginPage from './pages/user/LoginPage';
import RegisterPage from './pages/user/RegisterPage';
import SafetyPage from './pages/user/SafetyPage';
import EventsPage from './pages/user/EventsPage';
import EventDetailPage from './pages/user/EventDetailPage';
import VouchersPage from './pages/user/VouchersPage';
import QRDisplayPage from './pages/user/QRDisplayPage';
import WaitingRoomPage from './pages/user/WaitingRoomPage';
import GroupDetailPage from './pages/user/GroupDetailPage';
import PostEventPage from './pages/user/PostEventPage';
import ProfilePage from './pages/user/ProfilePage';
import ConnectionsPage from './pages/user/ConnectionsPage';
import NotificationsPage from './pages/user/NotificationsPage';
import PremiumDashboardPage from './pages/user/PremiumDashboardPage';
import InterestsPage from './pages/user/InterestsPage';
import KnowledgeBrowsePage from './pages/user/KnowledgeBrowsePage';
import CreateKnowledgeSessionPage from './pages/user/CreateKnowledgeSessionPage';
import MyKnowledgeSessionsPage from './pages/user/MyKnowledgeSessionsPage';
import ProfessionalProfilePage from './pages/user/ProfessionalProfilePage';
import GroupBoardPage from './pages/user/GroupBoardPage';
import OnboardingWizard from './pages/user/OnboardingWizard';
import VenuePublicPage from './pages/user/VenuePublicPage';
import VenuesBrowsePage from './pages/user/VenuesBrowsePage';
import StampLandingPage from './pages/user/StampLandingPage';
import MyLoyaltyPage from './pages/user/MyLoyaltyPage';
import LoyaltyCardDetailPage from './pages/user/LoyaltyCardDetailPage';
import TermsPage from './pages/user/TermsPage';
import PrivacyPage from './pages/user/PrivacyPage';
import PartnerApplyPage from './pages/user/PartnerApplyPage';
import PartnerApplicationStatusPage from './pages/user/PartnerApplicationStatusPage';
import NotFoundPage from './pages/user/NotFoundPage';

// ── Partner pages (lazy — only loaded after partner login) ──
const PartnerLoginPage = lazy(() => import('./pages/partner/PartnerLoginPage'));
const PartnerDashboard = lazy(() => import('./pages/partner/PartnerDashboard'));
const VenueProfilePage = lazy(() => import('./pages/partner/VenueProfilePage'));
const QRScannerPage = lazy(() => import('./pages/partner/QRScannerPage'));
const ManualLookupPage = lazy(() => import('./pages/partner/ManualLookupPage'));
const PartnerEventsPage = lazy(() => import('./pages/partner/PartnerEventsPage'));
const SettlementsPage = lazy(() => import('./pages/partner/SettlementsPage'));
const PartnerAnalyticsPage = lazy(() => import('./pages/partner/PartnerAnalyticsPage'));
const PartnerRevenuePage = lazy(() => import('./pages/partner/PartnerRevenuePage'));
const ProposeEventPage = lazy(() => import('./pages/partner/ProposeEventPage'));
const StaffPage = lazy(() => import('./pages/partner/StaffPage'));
const AvailabilityPage = lazy(() => import('./pages/partner/AvailabilityPage'));
const LoyaltySetupPage = lazy(() => import('./pages/partner/LoyaltySetupPage'));
const LoyaltyDashboardPage = lazy(() => import('./pages/partner/LoyaltyDashboardPage'));
const LoyaltyCustomersPage = lazy(() => import('./pages/partner/LoyaltyCustomersPage'));
const LoyaltyQRPage = lazy(() => import('./pages/partner/LoyaltyQRPage'));
const ManualStampPage = lazy(() => import('./pages/partner/ManualStampPage'));
const WebsiteEditorPage = lazy(() => import('./pages/partner/WebsiteEditorPage'));
const MicrositeTemplatePage = lazy(() => import('./pages/partner/MicrositeTemplatePage'));
const CustomDomainPage = lazy(() => import('./pages/partner/CustomDomainPage'));
const CRMDashboardPage = lazy(() => import('./pages/partner/CRMDashboardPage'));
const OffersPage = lazy(() => import('./pages/partner/OffersPage'));
const CampaignCreatePage = lazy(() => import('./pages/partner/CampaignCreatePage'));
const CampaignDetailPage = lazy(() => import('./pages/partner/CampaignDetailPage'));
const CustomerDetailPage = lazy(() => import('./pages/partner/CustomerDetailPage'));
const AutomationsPage = lazy(() => import('./pages/partner/AutomationsPage'));

// ── Admin pages (lazy — only loaded after admin login) ──
const AdminLoginPage = lazy(() => import('./pages/admin/AdminLoginPage'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminEventsPage = lazy(() => import('./pages/admin/AdminEventsPage'));
const AdminReportsPage = lazy(() => import('./pages/admin/AdminReportsPage'));
const AdminPartnersPage = lazy(() => import('./pages/admin/AdminPartnersPage'));
const AdminUsersPage = lazy(() => import('./pages/admin/AdminUsersPage'));
const AdminFinancePage = lazy(() => import('./pages/admin/AdminFinancePage'));
const AdminBlacklistPage = lazy(() => import('./pages/admin/AdminBlacklistPage'));
const AdminVenuesPage = lazy(() => import('./pages/admin/AdminVenuesPage'));
const AdminVenueDetailPage = lazy(() => import('./pages/admin/AdminVenueDetailPage'));
const AdminSupportPage = lazy(() => import('./pages/admin/AdminSupportPage'));
const AdminJobsPage = lazy(() => import('./pages/admin/AdminJobsPage'));
const AdminUserDetailPage = lazy(() => import('./pages/admin/AdminUserDetailPage'));
const AdminPartnerDetailPage = lazy(() => import('./pages/admin/AdminPartnerDetailPage'));
const AdminAnalyticsPage = lazy(() => import('./pages/admin/AdminAnalyticsPage'));
const AdminEventDetailPage = lazy(() => import('./pages/admin/AdminEventDetailPage'));
const AdminReportDetailPage = lazy(() => import('./pages/admin/AdminReportDetailPage'));
const AdminSettlementsPage = lazy(() => import('./pages/admin/AdminSettlementsPage'));
const AdminRefundsPage = lazy(() => import('./pages/admin/AdminRefundsPage'));
const AdminBroadcastPage = lazy(() => import('./pages/admin/AdminBroadcastPage'));
const AdminEventCalendarPage = lazy(() => import('./pages/admin/AdminEventCalendarPage'));
const AdminLedgerPage = lazy(() => import('./pages/admin/AdminLedgerPage'));
const AdminAuditLogPage = lazy(() => import('./pages/admin/AdminAuditLogPage'));
const AdminSettingsPage = lazy(() => import('./pages/admin/AdminSettingsPage'));
const AdminModerationPage = lazy(() => import('./pages/admin/AdminModerationPage'));

export default function App() {
  /**
   * Wildcard subdomain routing: `<slug>.coffeemates.biz/` swaps ONLY the `/`
   * route to `<Navigate>` → canonical `/venues/:slug` (deep links and
   * off-microsite links keep their own routes). Host is read once per mount
   * (can't change without full reload). `replace` avoids a `/` history entry
   * that re-bounces on back-button.
   */
  const host = typeof window !== 'undefined' ? window.location.host : '';
  const subdomainSlug = venueSlugFromHost(host);
  // BYO custom domain (mycafe.com → /venues/by-domain → /venues/<slug>);
  // only when host is not a CoffeeMates domain AND wildcard found no slug.
  const isByoHost = !subdomainSlug && isCustomDomain(host);

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* ── User App ── */}
        <Route element={<UserLayout />}>
          {/* Public */}
          <Route
            path="/"
            element={
              subdomainSlug
                ? <Navigate to={`/venues/${subdomainSlug}`} replace />
                : isByoHost
                  ? <BYODomainBoot host={host} />
                  : <HomePage />
            }
          />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/events/:id" element={<EventDetailPage />} />

          {/* Protected — requires login */}
          <Route path="/vouchers" element={<ProtectedRoute><VouchersPage /></ProtectedRoute>} />
          <Route path="/vouchers/:id/qr" element={<ProtectedRoute><QRDisplayPage /></ProtectedRoute>} />
          <Route path="/waiting/:voucherId" element={<ProtectedRoute><WaitingRoomPage /></ProtectedRoute>} />
          <Route path="/groups/current" element={<ProtectedRoute><GroupDetailPage /></ProtectedRoute>} />
          <Route path="/groups/:id" element={<ProtectedRoute><GroupDetailPage /></ProtectedRoute>} />
          <Route path="/groups/:id/board" element={<ProtectedRoute><GroupBoardPage /></ProtectedRoute>} />
          <Route path="/post-event/:eventId" element={<ProtectedRoute><PostEventPage /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/profile/connections" element={<ProtectedRoute><ConnectionsPage /></ProtectedRoute>} />
          <Route path="/profile/interests" element={<ProtectedRoute><InterestsPage /></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><PremiumDashboardPage /></ProtectedRoute>} />

          {/* Loyalty */}
          <Route path="/stamp/:venueToken" element={<StampLandingPage />} />
          <Route path="/loyalty" element={<ProtectedRoute><MyLoyaltyPage /></ProtectedRoute>} />
          <Route path="/loyalty/:cardId" element={<ProtectedRoute><LoyaltyCardDetailPage /></ProtectedRoute>} />

          <Route path="/knowledge" element={<KnowledgeBrowsePage />} />
          <Route path="/knowledge/create" element={<ProtectedRoute><CreateKnowledgeSessionPage /></ProtectedRoute>} />
          <Route path="/knowledge/my" element={<ProtectedRoute><MyKnowledgeSessionsPage /></ProtectedRoute>} />
          <Route path="/knowledge/pro/:userId" element={<ProfessionalProfilePage />} />
          <Route path="/partner/apply" element={<ProtectedRoute><PartnerApplyPage /></ProtectedRoute>} />
          <Route path="/partner/application-status" element={<ProtectedRoute><PartnerApplicationStatusPage /></ProtectedRoute>} />
          <Route path="/venues" element={<VenuesBrowsePage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/safety" element={<ProtectedRoute><SafetyPage /></ProtectedRoute>} />
        </Route>

        {/* Public venue microsite — deliberately OUTSIDE UserLayout so QR
            visitors don't see the app shell; templates bring their own
            scoped ThemeProvider. */}
        <Route path="/venues/:slug" element={<VenuePublicPage />} />

        {/* ── Auth (no layout) ── */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/onboarding" element={<ProtectedRoute><OnboardingWizard /></ProtectedRoute>} />

        {/* ── Partner Portal (lazy) ── */}
        <Route path="/partner/login" element={<PartnerLoginPage />} />
        <Route element={<PartnerLayout />}>
          <Route path="/partner" element={<ProtectedRoute requiredRole="partner"><PartnerDashboard /></ProtectedRoute>} />
          <Route path="/partner/venue" element={<ProtectedRoute requiredRole="partner"><VenueProfilePage /></ProtectedRoute>} />
          <Route path="/partner/scan" element={<ProtectedRoute requiredRole="partner"><QRScannerPage /></ProtectedRoute>} />
          <Route path="/partner/scan/manual" element={<ProtectedRoute requiredRole="partner"><ManualLookupPage /></ProtectedRoute>} />
          <Route path="/partner/events" element={<ProtectedRoute requiredRole="partner"><PartnerEventsPage /></ProtectedRoute>} />
          <Route path="/partner/settlements" element={<ProtectedRoute requiredRole="partner"><SettlementsPage /></ProtectedRoute>} />
          <Route path="/partner/analytics" element={<ProtectedRoute requiredRole="partner"><PartnerAnalyticsPage /></ProtectedRoute>} />
          <Route path="/partner/revenue" element={<ProtectedRoute requiredRole="partner"><PartnerRevenuePage /></ProtectedRoute>} />
          <Route path="/partner/events/propose" element={<ProtectedRoute requiredRole="partner"><ProposeEventPage /></ProtectedRoute>} />
          <Route path="/partner/automations" element={<ProtectedRoute requiredRole="partner"><AutomationsPage /></ProtectedRoute>} />
          <Route path="/partner/staff" element={<ProtectedRoute requiredRole="partner"><StaffPage /></ProtectedRoute>} />
          <Route path="/partner/availability" element={<ProtectedRoute requiredRole="partner"><AvailabilityPage /></ProtectedRoute>} />
          <Route path="/partner/loyalty" element={<ProtectedRoute requiredRole="partner"><LoyaltyDashboardPage /></ProtectedRoute>} />
          <Route path="/partner/loyalty/setup" element={<ProtectedRoute requiredRole="partner"><LoyaltySetupPage /></ProtectedRoute>} />
          <Route path="/partner/loyalty/qr" element={<ProtectedRoute requiredRole="partner"><LoyaltyQRPage /></ProtectedRoute>} />
          <Route path="/partner/loyalty/manual-stamp" element={<ProtectedRoute requiredRole="partner"><ManualStampPage /></ProtectedRoute>} />
          <Route path="/partner/loyalty/customers" element={<ProtectedRoute requiredRole="partner"><LoyaltyCustomersPage /></ProtectedRoute>} />
          <Route path="/partner/website" element={<ProtectedRoute requiredRole="partner"><WebsiteEditorPage /></ProtectedRoute>} />
          <Route path="/partner/microsite" element={<ProtectedRoute requiredRole="partner"><MicrositeTemplatePage /></ProtectedRoute>} />
          <Route path="/partner/custom-domain" element={<ProtectedRoute requiredRole="partner"><CustomDomainPage /></ProtectedRoute>} />
          <Route path="/partner/crm" element={<ProtectedRoute requiredRole="partner"><CRMDashboardPage /></ProtectedRoute>} />
          <Route path="/partner/crm/customers/:userId" element={<ProtectedRoute requiredRole="partner"><CustomerDetailPage /></ProtectedRoute>} />
          <Route path="/partner/offers" element={<ProtectedRoute requiredRole="partner"><OffersPage /></ProtectedRoute>} />
          <Route path="/partner/campaigns/new" element={<ProtectedRoute requiredRole="partner"><CampaignCreatePage /></ProtectedRoute>} />
          <Route path="/partner/campaigns/:id" element={<ProtectedRoute requiredRole="partner"><CampaignDetailPage /></ProtectedRoute>} />
        </Route>

        {/* ── Admin Dashboard (lazy) ── */}
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route element={<AdminLayout />}>
          <Route path="/admin" element={<ProtectedRoute requiredRole="admin"><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/analytics" element={<ProtectedRoute requiredRole="admin"><AdminAnalyticsPage /></ProtectedRoute>} />
          <Route path="/admin/events" element={<ProtectedRoute requiredRole="admin"><AdminEventsPage /></ProtectedRoute>} />
          <Route path="/admin/events/:id" element={<ProtectedRoute requiredRole="admin"><AdminEventDetailPage /></ProtectedRoute>} />
          <Route path="/admin/reports" element={<ProtectedRoute requiredRole="admin"><AdminReportsPage /></ProtectedRoute>} />
          <Route path="/admin/reports/:id" element={<ProtectedRoute requiredRole="admin"><AdminReportDetailPage /></ProtectedRoute>} />
          <Route path="/admin/venues" element={<ProtectedRoute requiredRole="admin"><AdminVenuesPage /></ProtectedRoute>} />
          <Route path="/admin/venues/:id" element={<ProtectedRoute requiredRole="admin"><AdminVenueDetailPage /></ProtectedRoute>} />
          <Route path="/admin/partners" element={<ProtectedRoute requiredRole="admin"><AdminPartnersPage /></ProtectedRoute>} />
          <Route path="/admin/partners/:id" element={<ProtectedRoute requiredRole="admin"><AdminPartnerDetailPage /></ProtectedRoute>} />
          <Route path="/admin/users" element={<ProtectedRoute requiredRole="admin"><AdminUsersPage /></ProtectedRoute>} />
          <Route path="/admin/users/:id" element={<ProtectedRoute requiredRole="admin"><AdminUserDetailPage /></ProtectedRoute>} />
          <Route path="/admin/finance" element={<ProtectedRoute requiredRole="admin"><AdminFinancePage /></ProtectedRoute>} />
          <Route path="/admin/blacklist" element={<ProtectedRoute requiredRole="admin"><AdminBlacklistPage /></ProtectedRoute>} />
          <Route path="/admin/support" element={<ProtectedRoute requiredRole="admin"><AdminSupportPage /></ProtectedRoute>} />
          <Route path="/admin/jobs" element={<ProtectedRoute requiredRole="admin"><AdminJobsPage /></ProtectedRoute>} />
          <Route path="/admin/settlements" element={<ProtectedRoute requiredRole="admin"><AdminSettlementsPage /></ProtectedRoute>} />
          <Route path="/admin/refunds" element={<ProtectedRoute requiredRole="admin"><AdminRefundsPage /></ProtectedRoute>} />
          <Route path="/admin/broadcast" element={<ProtectedRoute requiredRole="admin"><AdminBroadcastPage /></ProtectedRoute>} />
          <Route path="/admin/calendar" element={<ProtectedRoute requiredRole="admin"><AdminEventCalendarPage /></ProtectedRoute>} />
          <Route path="/admin/ledger" element={<ProtectedRoute requiredRole="admin"><AdminLedgerPage /></ProtectedRoute>} />
          <Route path="/admin/audit" element={<ProtectedRoute requiredRole="admin"><AdminAuditLogPage /></ProtectedRoute>} />
          <Route path="/admin/settings" element={<ProtectedRoute requiredRole="admin"><AdminSettingsPage /></ProtectedRoute>} />
          <Route path="/admin/moderation" element={<ProtectedRoute requiredRole="admin"><AdminModerationPage /></ProtectedRoute>} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
